import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  Clear as ClearIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  WifiOff as DisconnectedIcon,
  Wifi as ConnectedIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

function StreamPage() {
  const outputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [messageCount, setMessageCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);

  const addMessage = useCallback((message) => {
    if (isPaused) {
      setPendingMessages(prev => [...prev, message]);
      return;
    }

    const messageDiv = document.createElement('div');
    messageDiv.style.cssText = `
      padding: 8px 12px;
      margin: 4px 0;
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border-left: 3px solid #2196f3;
      border-radius: 6px;
      font-family: 'Roboto Mono', monospace;
      font-size: 14px;
      line-height: 1.4;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      animation: slideIn 0.3s ease-out;
      word-wrap: break-word;
    `;

    // Add timestamp
    const timestamp = new Date().toLocaleTimeString();
    const timestampSpan = document.createElement('span');
    timestampSpan.textContent = `[${timestamp}] `;
    timestampSpan.style.cssText = `
      color: #666;
      font-size: 12px;
      font-weight: 500;
    `;

    const contentSpan = document.createElement('span');
    contentSpan.textContent = message;
    contentSpan.style.color = '#333';

    messageDiv.appendChild(timestampSpan);
    messageDiv.appendChild(contentSpan);

    outputRef.current?.appendChild(messageDiv);
    outputRef.current?.scrollTo({
      top: outputRef.current.scrollHeight,
      behavior: 'smooth'
    });

    setMessageCount(prev => prev + 1);
  }, [isPaused]);

  const connect = useCallback(() => {
    const WS_BASE_URL = (window.location.protocol === "https:"
      ? "wss://" + window.location.host
      : "ws://" + window.location.host);

    const ws = new WebSocket(`${WS_BASE_URL}/ws/events`);
    wsRef.current = ws;
    setConnectionStatus('connecting');
    console.log(`Connecting to WebSocket at ${ws.url}`);

    ws.onopen = () => {
      setConnectionStatus('connected');
      console.log('WebSocket connected successfully');
    };

    ws.onmessage = (event) => {
      addMessage(event.data);
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
      console.warn('WebSocket closed. Reconnecting in 3s...');
      reconnectTimeout.current = setTimeout(connect, 3000);
    };

    ws.onerror = (err) => {
      setConnectionStatus('error');
      console.error('WebSocket error:', err);
      ws.close();
    };
  }, [addMessage]);

  const handleClear = () => {
    if (outputRef.current) {
      outputRef.current.innerHTML = '';
      setMessageCount(0);
    }
  };

  const handlePauseResume = () => {
    setIsPaused(prev => {
      if (prev && pendingMessages.length > 0) {
        // Resume and add pending messages
        pendingMessages.forEach(msg => addMessage(msg));
        setPendingMessages([]);
      }
      return !prev;
    });
  };

  const handleDownload = () => {
    const messages = Array.from(outputRef.current?.children || [])
      .map(div => div.textContent)
      .join('\n');

    const blob = new Blob([messages], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stream-log-${new Date().toISOString().slice(0, 19)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <ConnectedIcon sx={{ color: '#4caf50' }} />;
      case 'connecting':
        return <CircularProgress size={20} sx={{ color: '#ff9800' }} />;
      case 'disconnected':
      case 'error':
        return <DisconnectedIcon sx={{ color: '#f44336' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      case 'disconnected':
      case 'error': return 'error';
      default: return 'default';
    }
  };

  useEffect(() => {
    // Add CSS animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(-10px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(style);

    connect();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      document.head.removeChild(style);
    };
  }, [connect]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      {/* <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              🔴 Live Event Stream
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Real-time WebSocket event monitoring
            </Typography>
          </Box>
        </Box>
      </Paper> */}

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Tooltip title={isPaused ? "Resume stream" : "Pause stream"}>
            <IconButton
              onClick={handlePauseResume}
              color={isPaused ? "success" : "warning"}
              sx={{
                bgcolor: isPaused ? 'success.light' : 'warning.light',
                '&:hover': { bgcolor: isPaused ? 'success.main' : 'warning.main' }
              }}
            >
              {isPaused ? <PlayIcon /> : <PauseIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear all messages">
            <IconButton
              onClick={handleClear}
              color="error"
              sx={{
                bgcolor: 'error.light',
                '&:hover': { bgcolor: 'error.main' }
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download log">
            <IconButton
              onClick={handleDownload}
              color="primary"
              disabled={messageCount === 0}
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main' }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {/* Thêm phần trạng thái kết nối và messages count ở đây */}
          <Chip
            icon={getStatusIcon()}
            label={connectionStatus.toUpperCase()}
            color={getStatusColor()}
            variant="filled"
            sx={{ fontWeight: 600, ml: 2 }}
          />
          <Badge badgeContent={messageCount} color="secondary" max={9999}>
            <Chip
              label="Messages"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Badge>
          {isPaused && pendingMessages.length > 0 && (
            <Fade in>
              <Chip
                label={`${pendingMessages.length} pending`}
                color="warning"
                size="small"
                sx={{ ml: 2 }}
              />
            </Fade>
          )}
        </Box>
      </Paper>

      {/* Stream Output */}
      <Paper
        elevation={3}
        sx={{
          height: 500,
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #f0f2f5 0%, #e8eaf0 100%)',
          border: '1px solid #e0e0e0'
        }}
      >
        <Box
          ref={outputRef}
          sx={{
            height: '100%',
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
            },
          }}
        >
          {messageCount === 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              sx={{ color: '#666', textAlign: 'center' }}
            >
              <Typography variant="h6" sx={{ opacity: 0.6 }}>
                {connectionStatus === 'connecting' ? '🔄 Connecting to stream...' : '📡 Waiting for events...'}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Footer */}
      <Box mt={2} textAlign="center">
        <Typography variant="caption" color="text.secondary">
          Connection: {wsRef.current?.url || 'Not connected'} |
          Status: {connectionStatus} |
          Total Messages: {messageCount}
        </Typography>
      </Box>
    </Box>
  );
}

export default StreamPage;